
import React, { useEffect, useState, useCallback } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import cookie from 'js-cookie';
import './Footer';
import { jwtDecode } from 'jwt-decode';
import customTheme from './styles/themes/theme';
import NavBar from './components/common/navBar/navBar';
import { AuthContextProvider } from './context/AuthContext';
import { CommonDataContextProvider } from './context/commonDataContext';
import SessionExtensionPopup from './components/common/sessionExtensionPopup/SessionExtensionPopup';
import { Box } from '@mui/material';
import HeaderTop from './components/common/header/HeaderTop';
import { postHogUserAttributesTracking } from './utils/posthogUtil';
import { useMenuData } from './hooks/useMenuData';
import COMMON_CONSTANTS from './constants/commonConstants';
import { updatePortalSessions } from "./utils/authUtil";

/**
 * @desc It'll call the Express End-point /ccmc-website-services/validate-token to validate and 
 * Extend the Azure AD B2C Access Token, AMP JWT and AMC JWT token.
 * Return false for any error, or true for success.
 * @returns {Boolean}
 */
function validateAzureToken() {
  return fetch(COMMON_CONSTANTS.VALIDATE_TOKEN_ENDPOINT, {
    method: 'GET',
  }).then((res) => {
    if (!res.ok) {
      return false;
    } else {
        updatePortalSessions();
    }
  }).then((data) => {
    return true;
  });
}

/**
 * @desc It'll update the sessionStorage based on the validateAzureToken().
 * @returns {Boolean}
 */
function validateAzureGWPortalSession() {
  if(window.location.pathname == '/account-summary' && window.location.hash == '#/home') {
    // Skip Azure Token validation after login and for Account Summary Landing Page
    return true;
  } else {
    console.log('validateLRGWSession called ...');
    if (!validateAzureToken()) {
      console.log('In expired session');
      clearAllSessionStorage();
      // Redirect to signin page
      window.location.href = COMMON_CONSTANTS.SIGN_IN_URL;
      return false;
    } else {
      console.log('In active session');
      extendAmpGWPortalToken();
      extendAmcFnolToken();
      return true;
    }
  }
}

/**
 * @desc It'll set the Amp_User_Jwt in the sessionStorage based on the existing Cookies value.
 * @returns {void}
 */
function extendAmpGWPortalToken() {
  if (!cookie.get(COMMON_CONSTANTS.AUTH_SESSIONS.AMP_USER_JWT_COOKIE_NAME)) {
    clearAllSessionStorage();
    window.location.href = COMMON_CONSTANTS.SIGN_IN_URL;
  } else {
      updatePortalSessions()
  }
}

/**
 * @desc It'll set the Amc_User_Jwt and CC_GWUserContext in the sessionStorage based on the existing Cookies value.
 * @returns {void}
 */
function extendAmcFnolToken() {
  console.log('extendAmcFnolToken called ...');

  if (!cookie.get(COMMON_CONSTANTS.AUTH_SESSIONS.AMC_USER_JWT_COOKIE_NAME) && !cookie.get(COMMON_CONSTANTS.AUTH_SESSIONS.AMP_USER_JWT_COOKIE_NAME)) {
    clearAllSessionStorage();
    window.location.href = COMMON_CONSTANTS.SIGN_IN_URL;
  } else if (cookie.get(COMMON_CONSTANTS.AUTH_SESSIONS.AMC_USER_JWT_COOKIE_NAME) && !cookie.get(COMMON_CONSTANTS.AUTH_SESSIONS.AMP_USER_JWT_COOKIE_NAME)) {
    clearAllSessionStorage();
    window.location.href = COMMON_CONSTANTS.SIGN_IN_URL;
  } else if (cookie.get(COMMON_CONSTANTS.AUTH_SESSIONS.AMC_USER_JWT_COOKIE_NAME) && cookie.get(COMMON_CONSTANTS.AUTH_SESSIONS.CC_GWUSERCONTEXT_COOKIE_NAME) && cookie.get(COMMON_CONSTANTS.AUTH_SESSIONS.AMP_USER_JWT_COOKIE_NAME)) {
    updatePortalSessions();
  }
  console.log('extendAmcFnolToken called ended ...');
}

/**
 * @desc It'll clear all the sessionStorage values based on the existing Cookies value.
 * @returns {void}
 */
function clearAllSessionStorage() {
  const validateSessionState = cookie.get(COMMON_CONSTANTS.AUTH_SESSIONS.AMP_USER_JWT_COOKIE_NAME); // Adjust as needed

  if (!validateSessionState || validateSessionState == null) {
    sessionStorage.removeItem(COMMON_CONSTANTS.AUTH_SESSIONS.AMC_USER_JWT_COOKIE_NAME);
    sessionStorage.removeItem(COMMON_CONSTANTS.AUTH_SESSIONS.AMP_USER_JWT_COOKIE_NAME);
    sessionStorage.removeItem(COMMON_CONSTANTS.AUTH_SESSIONS.AB_GWUSERCONTEXT_COOKIE_NAME);
    sessionStorage.removeItem(COMMON_CONSTANTS.AUTH_SESSIONS.CC_GWUSERCONTEXT_COOKIE_NAME);
    sessionStorage.removeItem(COMMON_CONSTANTS.AUTH_SESSIONS.PC_GWUSERCONTEXT_COOKIE_NAME);
    sessionStorage.removeItem(COMMON_CONSTANTS.AUTH_SESSIONS.BC_GWUSERCONTEXT_COOKIE_NAME);
  }
}

/**
 * @desc It'll call the Express End-point /ccmc-website-services/logging-portal to log the GW Portal request based on portalType.
 * @param body request Object {request, response, processFlow, policyNumber}    
 * @param portalType should be within [AMP_Billing ,AMP_General ,AMC, QBP, QBP_Payment] for logging.  
 * @returns {{result : 'Logged'} | Error}
 */
function logGWPortalRequest(body, portalType) {
  body.portalType = portalType;
  return fetch(COMMON_CONSTANTS.LOGGING_PORTAL_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  }).then(res => {
    return res.data;
  });
}

/**
 * @desc It'll call the Express End-point /ccmc-website-services/logging-navigation 
 * That will create file name for respective GW Portal example app-QBP-<MM-DD-YYYY>.log and place all the logs inside that respective GW Portal folder.
 * @param body requested Object {request, response, processFlow, policyNumber}
 * @param portalType should be within [AMP_Billing ,AMP_General ,AMC, QBP, QBP_Payment] for logging.
 * @returns {{result : 'Logged'} | Error}
 */
function loggingGWPortalNavigations(body, portalType) {
  body.portalType = portalType;
  return fetch(COMMON_CONSTANTS.LOGGING_NAVIGATION_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  }).then(res => {
    return res.data;
  });
}

/**
 * @desc For tracking GW Portal Steps, Events and Focus out event for implemented field
 * the events are tacked in GA4 Via GTM.
 * @param data Requested Object
 * @returns {void | Error}
 */
function trackGWStepOrEvent(data) {
  var isSignedIn = cookie.get(COMMON_CONSTANTS.AUTH_SESSIONS.AMP_USER_JWT_COOKIE_NAME) ? true : false;

  data.visitor_type = isSignedIn ? "customer" : "guest";

  console.log("Custom STEP/Event with data " + JSON.stringify(data) + " from QBP.. ");

  if (data == undefined || data.length == 0) {
    return;
  }

  // PostHog Custom User Attributes tracking and Filtering
  if(data.event && data.event == 'exit_form_field' && (data.field_name == 'First Name' || data.field_name == 'Last Name')) {
    postHogUserAttributesTracking();
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
}

/**
 * @desc For tracking SPA page navigations events, action,
 * It'll add the current page url in window dataLayer which is pushed to GA4 via GTM.
 * @param url  Indicates the current page path.
 * @returns {void | Error}
 */
function trackSPAactions(url) {
  console.log("Requested action url '" + url + "' from GW Portals SPA.. ");
  if (url?.length > 0) {
    url = url.replace(window.location.origin, "");
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'Pageview',
      'pagePath': url,
      'pageTitle': document.title //some arbitrary name for the page/state
    });
    //PostHog Custom User Attributes tracking and Filtering
    postHogUserAttributesTracking();
  }
}

/**
 * @desc For tracking GW Portal page navigations events it'll pass the current page url in window dataLayer which is pushed to GA4 via GTM.
 * It'll call the express end-point /ccmc-website-services/logging-navigation for logging the QBP and AMC page navigations.
 * @param url  Indicates the current page path.
 * @param portalType  Indicates the current page path.
 * @returns {void | Error}
 */
function trackGWPortalGAandLogNavigation(url, portalType) {
  console.log("Requested action url '" + url + "' from AMP or QBP or FNOL.. ");
  if (url == undefined || url.length == 0) {
    url = window.location.href;
  }
  // Check if GW QBP and AMP Base route path is included in URL then push GA tracking page view and event
  if (url.includes("/quoting/") || url.includes("/account-summary") || url.includes("/account-management-claims")) {
    url = url.replace(window.location.origin, "");
    //Added for GTM:: Handle to Push the Pageview event from GTM to GA
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'Pageview',
      'pagePath': url,
      'pageTitle': document.title //some arbitrary name for the page/state
    });
    console.log("Added action url '" + url + "' to google analytics..");
    if (url.includes("/quoting/") || url.includes("/account-management-claims")) {
      loggingGWPortalNavigations({ url: url }, portalType);
    }
    postHogUserAttributesTracking();
  }
}

window.postHogUserAttributesTracking = postHogUserAttributesTracking.bind();
window.validateAzureToken = validateAzureToken.bind();
window.validateAzureGWPortalSession = validateAzureGWPortalSession.bind();
window.extendAmcFnolToken = extendAmcFnolToken.bind();
window.extendAmpGWPortalToken = extendAmpGWPortalToken.bind();
window.clearAllSessionStorage = clearAllSessionStorage.bind();
window.logGWPortalRequest = logGWPortalRequest.bind();
window.loggingGWPortalNavigations = loggingGWPortalNavigations.bind();
window.trackGWPortalGAandLogNavigation = trackGWPortalGAandLogNavigation.bind();
window.trackSPAactions = trackSPAactions.bind();
window.trackGWStepOrEvent = trackGWStepOrEvent.bind();

// React component
export default function App() {

  const [showGuestHeader, setGuestHeader] = useState(true);
  // quoting_footer_menu in WordPress carries both footer menu items and ACF cpConfigs (profile_domain, org_id) for Customer Profile script on quoting routes
  const quotingMenuData = useMenuData('quoting_footer_menu');
  const cpConfigs = quotingMenuData?.cpConfigs ?? {};
  const [trackingScriptAdded, setTrackingScriptAdded] = useState(false);
  const intercomChatAppId = process.env.REACT_APP_INTERCOM_CHAT_APP_ID;

  const customMrktgJSURL = process.env.REACT_APP_LNTMXIQ_ADVANCED_PROFILING_TOOLKIT_JS;

  function includeScript() {
    var script = document.createElement('script');
    script.type = 'text/javascript';

    //GTM Script Include
    var jsCode = `
    dataLayer = [];
      (function(a,s,y,n,c,h,i,d,e){s.className+=' '+y;h.start=1*new Date;
    h.end=i=function(){s.className=s.className.replace(RegExp(' ?'+y),'')};
    (a[n]=a[n]||[]).hide=h;setTimeout(function(){i();h.end=null},c);h.timeout=c;
    })(window,document.documentElement,'async-hide','dataLayer',2000,
    {'${process.env.REACT_APP_GTM_ID}':true});
    `;

    script.textContent = jsCode;
    // Append the script element to the <head>
    document.head.appendChild(script);

    var gtmScript = document.createElement('script');
    gtmScript.type = 'text/javascript';

    //GTM JS File Include
    const gtmScriptJsCode = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    '//www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${process.env.REACT_APP_GTM_ID}');
    `
    gtmScript.textContent = gtmScriptJsCode;
    // Append the script element to the <head>
    document.head.appendChild(gtmScript);
  }

  /**
   * Intercom Chat Script Include before end of body Tag
   */
  function includeIntercomChatScript() {
    var intercomSettingScript = document.createElement('script');
    intercomSettingScript.type = 'text/javascript';

    //GTM Script Include
    var intercomSettingJSCode = `window.intercomSettings = {
      api_base: "https://api-iam.intercom.io",
      app_id: "${intercomChatAppId}"
    };`;

    intercomSettingScript.textContent = intercomSettingJSCode;
    // Append the script element to the <head>
    document.body.appendChild(intercomSettingScript);

    var intercomScript = document.createElement('script');
    intercomScript.type = 'text/javascript';

    //GTM JS File Include
    const intercomScriptJSCode = `
    // We pre-filled your app ID in the widget URL: 'https://widget.intercom.io/widget/e998z49r'
  (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/${intercomChatAppId}';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();`;
    intercomScript.textContent = intercomScriptJSCode;

    // Append the script element to the <head>
    document.body.appendChild(intercomScript);

    if (cookie.get(COMMON_CONSTANTS.AUTH_SESSIONS.USER_SESSION_COOKIE_NAME)) {
      const userSession = cookie.get(COMMON_CONSTANTS.AUTH_SESSIONS.USER_SESSION_COOKIE_NAME);

      try {
        if (window.Intercom) {
          const userSessionDecoded = jwtDecode(userSession);
          if (userSessionDecoded?.intercomChatUserHash) {
            let intercomUserMetaData = {
              "name": userSessionDecoded.firstName + " " + userSessionDecoded.lastName,
              "email": userSessionDecoded.emailAddress,
              "user_hash": userSessionDecoded.intercomChatUserHash
            }
            window.Intercom('update', intercomUserMetaData);
          }
        }
      } catch (err) {
        console.log('Error occurred while decoding User Session : ' + err)
      }
    }
  }

  // Constants for script management
  const SCRIPT_CHECK_INTERVAL = 500; // 500ms
  const SCRIPT_TIMEOUT = 10000; // 10 seconds

  /**
   * Check if Customer Profile script is already loaded
   */
  function isCustomerProfileScriptLoaded() {
    // Check if the global cmmrktgfp function is available
    return typeof window.cmmrktgfp === 'object' && window.cmmrktgfp !== null;
  }

  /**
   * Check if tracking script is already added
   */
  function isTrackingScriptAdded() {
    // Check for scripts with CP tracking content
    const scripts = document.querySelectorAll('script');
    for (let script of scripts) {
      if (script.textContent && script.textContent.includes('cmmrktgfp.cp(')) {
        return true;
      }
    }
    return false;
  }

  /**
   * 
   * Get Customer Profile configuration from WordPress cpConfigs
   */
  function getCustomerProfileConfig() {

    // Use cpConfigs from WordPress 
    const profileDomain = cpConfigs?.profile_domain;
    const orgId = cpConfigs?.org_id;
    
    return { profileDomain, orgId };
  }

  /**
   * Include customer profile script when route is quoting and profile_domain and org_id environment variables are available
   */
  function addCustomerProfileScript() {
    // Check if script is already loaded to prevent duplicates
    if (isCustomerProfileScriptLoaded()) {
      addCpTrackingScript();
      return;
    }

    const { profileDomain, orgId } = getCustomerProfileConfig();

    if (profileDomain && orgId) {
      const cpScript = document.createElement('script');
      cpScript.type = 'text/javascript';
      cpScript.src = customMrktgJSURL;
      
      cpScript.onload = () => {
        addCpTrackingScript();
      };
      
      document.head.appendChild(cpScript);
      
      // Set up interval to check for script loading with proper cleanup
      let checkInterval = null;
      let timeoutId = null;
      
      checkInterval = setInterval(() => {
        if(isCustomerProfileScriptLoaded()){
          // Script is loaded, add tracking script and clear interval
          addCpTrackingScript();
          clearInterval(checkInterval);
          clearTimeout(timeoutId);
        }
      }, SCRIPT_CHECK_INTERVAL);

      // Set a timeout to prevent infinite checking
      timeoutId = setTimeout(() => {
        clearInterval(checkInterval);
      }, SCRIPT_TIMEOUT);
      
    } 
  }

  function addCpTrackingScript(){
    // Check if Customer Profile script is already loaded
    if(isCustomerProfileScriptLoaded()){
      // Add the tracking script immediately if already loaded
      addTrackingScript();
    }
  }

  function addTrackingScript() {
    // Check if tracking script is already added to avoid duplicates
    if (isTrackingScriptAdded()) {
      return;
    }

    const { profileDomain, orgId } = getCustomerProfileConfig();
    const CPJSESSIONID = getCPJSESSIONID();

    if(!CPJSESSIONID || !profileDomain || !orgId) {
      return;
    }

    const trackingScript = document.createElement('script');
    trackingScript.type = 'text/javascript';
    
    trackingScript.textContent = `
      var CPJSESSIONID = '${CPJSESSIONID}';
      if (typeof cmmrktgfp === "object") { 
        cmmrktgfp.cp('${profileDomain}', '${orgId}', CPJSESSIONID);	
      } 
    `;
    
    document.head.appendChild(trackingScript);
    setTrackingScriptAdded(true);
  }

  function getCPJSESSIONID() {
    try {
      const portalSessionCookie = cookie.get('portalSession');
      
      if (!portalSessionCookie || portalSessionCookie.length === 0) {
        return null;
      }
      
      const portalSessionJson = JSON.parse(portalSessionCookie);
      return portalSessionJson?.cpJSESSIONID || null;
    } catch (error) {
      return null;
    }
  }

  // Memoize the function using useCallback
  const handleTabPress = useCallback((event) => {
    if (event.key === 'Tab') {
      document.body.classList.remove('disable-focus-state');
    }
  }, []); // Empty dependency array ensures the function is memoized once

  // Memoize the function using useCallback
  const handleMouseDown = useCallback((event) => {
    document.body.classList.add('disable-focus-state');
  }, []);


  useEffect(() => {
    // Add event listener for keydown event
    window.addEventListener('keydown', handleTabPress);

    // Add event listener for mousedown event
    window.addEventListener('mousedown', handleMouseDown);

    // Disable Focus
    document.body.classList.add('disable-focus-state');

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('keydown', handleTabPress);
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, [handleTabPress, handleMouseDown]); // Only re-run effect if handleTabPress changes

  useEffect(() => {
    if (cookie.get(COMMON_CONSTANTS.AUTH_SESSIONS.AMP_USER_JWT_COOKIE_NAME) || cookie.get(COMMON_CONSTANTS.AUTH_SESSIONS.AMC_USER_JWT_COOKIE_NAME)) {
      setGuestHeader(false);
    }
    includeScript();

    if (intercomChatAppId) {
      includeIntercomChatScript();
    }
  }, []);

  useEffect(() => {
    if(trackingScriptAdded){
      return;
    }

    // Check if current route is quoting
    const isQuotingRoute = window.location.pathname.includes('/quoting');

    if (isQuotingRoute && cpConfigs?.profile_domain?.length > 0 && cpConfigs?.org_id?.length > 0) {
      addCustomerProfileScript();
    }

    // Cleanup function to remove any tracking scripts when component unmounts
    /*return () => {
      const scripts = document.querySelectorAll('script');
      for (let script of scripts) {
        if (script.textContent && script.textContent.includes('cmmrktgfp.cp(')) {
          script.remove();
        }
        if (script.src && script.src.includes('cm-mrktg-fplib.js')) {
          script.remove();
        }
      }
    };*/
  }, [cpConfigs, trackingScriptAdded]);

  return (
    <AuthContextProvider>
      <CommonDataContextProvider>
        <ThemeProvider theme={customTheme}>
          <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
            <HeaderTop />
          </Box>
          <NavBar position="sticky" elevation={3} actions={{}} />
          <SessionExtensionPopup />
        </ThemeProvider>
      </CommonDataContextProvider>
    </AuthContextProvider>
  );
}
